// Modules
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@global/store/hooks';
import { useAppSelector } from '@global/store/hooks';

// CSS and assets
import '@pages/checkout/components/BillingInformation/style.scss';

// Components
import { useModal } from '@global/hooks/useModal';
import { useAutocomplete, SuggestAddress } from '@global/hooks/useAutocomplete';
import { ReloadInitResponseContext } from '@pages/checkout/components/SecurePayment';

import { BillingInfoForm } from '@global/components/BillingInfoForm/BillingInfoForm';
import { Spinner } from '@global/components/UI Elements/Spinner';
import { Addresses } from '@global/components/Addresses';
import { RestrictedCountryModal } from '@global/components/RestrictedCountryModal';

// Redux
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import {
  cartSelector,
  subscriptionFrequencySelector,
} from '@global/store/slices/cart/selectors';
import { updateBillingInformation } from '@global/store/slices/cart/extraReducers/updateBillingInformation';
import { toggleAgreementCheck } from '@global/store/slices/cart/cartSlice';

// Utils
import { useYupValidationResolver } from '@global/components/ActiveForm/utils';
import { billingInformationSchema } from '@pages/checkout/components/BillingInformation/rules';
import {
  constructBillingInfo,
  setZipCodeFormData,
  getStateFromDropdown,
} from '@pages/checkout/components/BillingInformation/helper';
import { buildStringWithTitleAndVat } from '@global/utils/lang';
import { OrderItemType } from '@global/utils/orderUtils';
import { CountryService } from '@global/utils/CountryService';
import { getLocaleConfig } from '@global/localization/services';
import { filterSavedAddressesByFallbackCountry } from '@pages/checkout/helper';

// Types
import { Address, AddressType, LOCALE_CODES } from '@global/types/types';
import {
  BillingInfoFormValuesType,
  EMPTY_BILLING_FORM_VALUES,
} from '@global/components/BillingInfoForm/types';
import { BillingFormType } from '@src/global/components/BillingInfoForm/types';
import { useSavedCustomerAddresses } from '@src/global/hooks/useSavedCustomerAddresses';
import {
  BILLING_US_REGION_RESTRICTED_COUNTRIES,
  SHIPPING_REGION,
} from '../../consts';
import { ExistingAddress } from '@src/global/components/ExistingAddress';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface IProps {
  isLoading: boolean;
  checkoutStep?: number;
  buttonId: string;
  payment?: Record<string, unknown>;
  paymentFormError?: string | null;
}

export const BillingInformation: React.FC<IProps> = ({
  isLoading,
  checkoutStep,
  buttonId,
  payment,
  paymentFormError,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as LOCALE_CODES;

  const dispatch = useAppDispatch();
  const cartState = useAppSelector(cartSelector);
  const countries = useAppSelector(countriesSelector);

  const resolverSchema = billingInformationSchema();
  const rulesResolver = useYupValidationResolver(resolverSchema);

  //React Hook Form
  const methods = useForm<BillingInfoFormValuesType>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: rulesResolver,
  });

  const { handleOpen: openRestrictedCountryModal } = useModal<OrderItemType[]>({
    hideCloseButton: true,
    customClass: 'restricted-modal',
    customOverlayClass: 'restricted-modal-overlay',
    content: ({ handleClose, customData }) => (
      <RestrictedCountryModal
        type="billing"
        handleClose={handleClose}
        customData={customData}
      />
    ),
  });

  const filterSavedAddresses = useCallback(
    filterSavedAddressesByFallbackCountry(
      countries,
      cartState.isPhysical,
      cartState.isSubscription
    ),
    [countries, cartState.isPhysical, cartState.isSubscription]
  );

  const {
    savedCustomerAddresses,
    selectedAddress,
    selectedAddressUid,
    isNewAddress,

    // actions
    editAddress,
    selectAddress,
    createNewAddress,
  } = useSavedCustomerAddresses(AddressType.BILLING, filterSavedAddresses);

  const { setPaymentErr } = useContext(ReloadInitResponseContext);

  const [isCartButtonLoading, setIsCartButtonLoading] =
    useState<boolean>(false);

  const vatTitle = buildStringWithTitleAndVat(
    language,
    t('checkout.summary.tax'),
    checkoutStep
  );
  const subscriptionFrequency = useAppSelector(subscriptionFrequencySelector);
  const frequency =
    t(`subscription.${subscriptionFrequency}.capitalizedFrequency`) || '';

  const agreementText = cartState.isSubscriptionExist
    ? t('checkout.step2.agreement', { vatTitle, frequency })
    : null;

  const restrictedCountries = cartState.shippingInfo.uid
    ? BILLING_US_REGION_RESTRICTED_COUNTRIES
    : [];

  const region = cartState.shippingInfo.uid ? SHIPPING_REGION : null;

  const [isBillingAddressAsShipping, setIsBillingAddressAsShipping] =
    useState<boolean>(
      !!cartState.shippingInfo.uid && !cartState.billingInfo.city
    );

  const setAddressFormData = (address: Partial<Address>) => {
    methods.setValue('country', address.wCountry);
    methods.setValue('address1', address.upaLine1);
    methods.setValue('address2', address.upaLine2);
    methods.setValue('city', address.upaCity);
    setZipCodeFormData(address.upaPostcode, address.wCountry, methods);
    methods.setValue('state', address.gadStateOrProvince);
    methods.setValue('phone', address.upaPhoneNumber);
    methods.setValue(
      'CCname',
      `${address.wFirstName || ''} ${address.wLastName || ''}`.trim()
    );
  };

  const setAddressFormRegionData = (data: SuggestAddress) => {
    const countryCode = methods.getValues('country');
    const country = CountryService.getCountryByIsoCode(countries, countryCode);
    if (!country) return;
    const { stateDropdown } = getLocaleConfig(country?.isoCodeTwo);
    const state = stateDropdown
      ? CountryService.getStateByName(country.regions, data.state)?.isoCodeShort
      : '';

    methods.setValue('city', data.city);
    methods.setValue('state', state);
    methods.trigger(['city', 'state']);
  };

  const updatePaymentInfoFormSubmitHandler = async (
    data: BillingInfoFormValuesType
  ) => {
    setIsCartButtonLoading(true);

    const isSelectedAddressNew = isNewAddress || isBillingAddressAsShipping;

    try {
      const dataBillingInfo = constructBillingInfo(
        cartState.billingInfo,
        data,
        countries,
        isSelectedAddressNew
      );

      dispatch(updateBillingInformation({ dataBillingInfo }));
    } finally {
      setIsCartButtonLoading(false);
    }
  };

  const onChangeSuggestions = useCallback((suggestions: SuggestAddress[]) => {
    const suggestedAddress = suggestions[0];
    if (suggestedAddress) setAddressFormRegionData(suggestedAddress);
  }, []);

  const { onChangeZipCode } = useAutocomplete({ onChangeSuggestions });

  useEffect(() => {
    if (selectedAddress) setAddressFormData(selectedAddress);
    if (!selectedAddress) methods.reset(EMPTY_BILLING_FORM_VALUES);

    // Autocomplete with zip code
    const subscription = methods.watch((value, { name, type }) => {
      if (
        name === 'zip' &&
        selectedAddress?.upaPostcode !== value.zip &&
        type === 'change'
      ) {
        onChangeZipCode(value.zip, value.country);

        // Format postal code as user types (auto-formats for supported countries)
        setZipCodeFormData(value.zip, value.country, methods);
      }
    });

    return () => subscription.unsubscribe();
  }, [selectedAddress]);

  useEffect(() => {
    if (paymentFormError) {
      setIsCartButtonLoading(false);
    }
  }, [paymentFormError]);

  useEffect(() => {
    if (payment) {
      const formData = methods.getValues();
      if (formData.country) updatePaymentInfoFormSubmitHandler(formData);
      setIsCartButtonLoading(true);
    }
  }, [payment]);

  const handleUseBillingAddressAsShipping = () => {
    setIsBillingAddressAsShipping((prevState) => !prevState);
  };

  const handleCreateNewAddress = () => {
    createNewAddress();
    methods.reset(EMPTY_BILLING_FORM_VALUES);
  };

  useEffect(() => {
    if (isBillingAddressAsShipping) {
      setAddressFormData({
        uid: cartState.shippingInfo.uid,
        gadStateOrProvince: cartState.shippingInfo.state,
        upaCity: cartState.shippingInfo.city,
        upaLine1: cartState.shippingInfo.addressOne,
        upaLine2: cartState.shippingInfo.addressTwo,
        upaPhoneNumber: cartState.shippingInfo.phone,
        upaPostcode: cartState.shippingInfo.zip,
        wCountry: cartState.shippingInfo.country.iso2Code,
        wFirstName: cartState.shippingInfo.firstName,
        wLastName: cartState.shippingInfo.lastName,
        wLine3: '',
      });
      return;
    }

    if (selectedAddress) {
      setAddressFormData({
        uid: selectedAddress?.uid,
        gadStateOrProvince: selectedAddress.gadStateOrProvince,
        upaCity: selectedAddress.upaCity,
        upaLine1: selectedAddress.upaLine1,
        upaLine2: selectedAddress.upaLine2,
        upaPhoneNumber: selectedAddress.upaPhoneNumber,
        upaPostcode: selectedAddress.upaPostcode,
        wCountry: selectedAddress.wCountry,
        wFirstName: selectedAddress.wFirstName,
        wLastName: selectedAddress.wLastName,
        wLine3: selectedAddress.wLine3,
      });
      return;
    }

    if (cartState.billingInfo.name) {
      setAddressFormData({
        uid: cartState.billingInfo.uid,
        gadStateOrProvince: getStateFromDropdown(
          cartState?.billingInfo?.country?.iso2Code,
          cartState.billingInfo.state
        ),
        upaCity: cartState.billingInfo.city,
        upaLine1: cartState.billingInfo.addressOne,
        upaLine2: cartState.billingInfo.addressTwo,
        upaPhoneNumber: cartState.billingInfo.tel,
        upaPostcode: cartState.billingInfo.zip,
        wCountry: cartState.billingInfo.country.iso2Code,
        wFirstName: cartState.billingInfo.name.split(' ')[0],
        wLastName: cartState.billingInfo.name.split(' ')[1],
        wLine3: '',
      });
      return;
    }

    methods.reset(EMPTY_BILLING_FORM_VALUES);
  }, [isBillingAddressAsShipping, selectedAddress, cartState.billingInfo]);

  return isLoading ? (
    <div style={{ padding: '20px' }}>
      <Spinner size={'64px'} />
    </div>
  ) : (
    <div className="billing-info">
      <span className="billing-info__subtitle regularText">
        {t('checkout.step2.subTitle')}
      </span>
      <span className="billing-info__info regularText">
        {t('checkout.step2.instructions')}
      </span>
      {cartState.shippingInfo?.uid && (
        <ExistingAddress
          isSelected={isBillingAddressAsShipping}
          onSelect={handleUseBillingAddressAsShipping}
          address={cartState.shippingInfo}
        />
      )}

      <Addresses
        isHidden={isBillingAddressAsShipping}
        addresses={savedCustomerAddresses}
        selectedAddressUid={selectedAddressUid}
        selectAddress={selectAddress}
        editAddress={editAddress}
        createNewAddress={handleCreateNewAddress}
      />
      <FormProvider {...methods}>
        <BillingInfoForm
          enabledAddressDoctor={isNewAddress && !isBillingAddressAsShipping}
          billingInfo={cartState.billingInfo}
          buttonId={buttonId}
          buttonText={t('checkout.step2.cta')}
          isLoading={cartState.isLoading}
          toggleAgreementCheck={() => {
            dispatch(toggleAgreementCheck());
          }}
          isCartButtonLoading={isCartButtonLoading}
          setIsCartButtonLoading={setIsCartButtonLoading}
          agreementText={agreementText}
          agreementCheck={cartState.agreementCheck}
          formType={BillingFormType.BillingForm}
          onCountryRestrictedProducts={openRestrictedCountryModal}
          hiddenForm={
            Boolean(selectedAddressUid) || Boolean(isBillingAddressAsShipping)
          }
          setPaymentErr={setPaymentErr}
          withPaymentForm
          isCountrySelectDisabled={
            cartState.isSubscription &&
            ['en-gb', 'de-de'].includes(SiteConfig.getConfig().locale)
          }
          disableCountryLocaleFallback
          region={region}
          restrictedCountries={restrictedCountries}
          isPhysical={cartState.isPhysical}
        />
      </FormProvider>
    </div>
  );
};
